// /app/frontend/src/components/frugal-v2/components/UserMenu.jsx
// User dropdown menu with sections (170 lines)

import React, { useState } from 'react';
import { 
  User, LogOut, Settings, Shield, Palette,
  Mail, FileText, Calendar, HelpCircle, ChevronDown, Globe, CalendarClock, Activity, History
} from 'lucide-react';

const UserMenu = ({ 
  user, 
  isOpen, 
  onClose, 
  onLogout, 
  onLanguageChange,
  onCloseMonth,
  onOpenBankEmail,
  onOpenSubscription,
  onOpenMonthSummary,
  onOpenCashflow,
  onOpenMonthEmails,
  onOpenScheduledCharges,
  onOpenSupport,
  onOpenZenLock,
  onOpenCurtainAdmin,
  onOpenReportsHistory,
  currentLanguage,
  t 
}) => {
  const [expandedSection, setExpandedSection] = useState(null);
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-30" onClick={onClose} />
      
      {/* Menu */}
      <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-white/10 rounded-xl shadow-lg py-2 min-w-56 z-50">
        {/* ADMIN (solo para admins) */}
        {isAdmin && (
          <>
            <div className="px-3 py-1">
              <button
                onClick={() => toggleSection('admin')}
                className="w-full flex items-center justify-between text-xs font-semibold text-amber-500 uppercase tracking-wider"
              >
                <span className="flex items-center gap-2">
                  <Shield className="w-3 h-3" />
                  Admin
                </span>
                <ChevronDown className={`w-3 h-3 transition-transform ${expandedSection === 'admin' ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {expandedSection === 'admin' && (
              <div className="px-2 py-1 space-y-1">
                <button 
                  onClick={() => {
                    onOpenCurtainAdmin?.();
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-amber-300 hover:bg-amber-500/10 rounded-lg"
                >
                  <Palette className="w-4 h-4" />
                  {t('frugal.admin.curtainConfig')}
                </button>
                {user?.can_grant_subscriptions && (
                  <button 
                    onClick={() => {
                      window.location.href = '/admin';
                    }}
                    data-testid="menu-admin-panel-btn"
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-amber-300 hover:bg-amber-500/10 rounded-lg"
                  >
                    <Shield className="w-4 h-4" />
                    Conceder Trial
                  </button>
                )}
              </div>
            )}
          </>
        )}
        
        {/* CONFIGURACIÓN */}
        <div className="px-3 py-1">
          <button
            onClick={() => toggleSection('config')}
            className="w-full flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider"
          >
            <span className="flex items-center gap-2">
              <Settings className="w-3 h-3" />
              {t('frugal.menu.configuration')}
            </span>
            <ChevronDown className={`w-3 h-3 transition-transform ${expandedSection === 'config' ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {expandedSection === 'config' && (
          <div className="px-2 py-1 space-y-1">
            <button 
              onClick={() => {
                if (onOpenBankEmail) onOpenBankEmail();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 rounded-lg"
            >
              <Mail className="w-4 h-4" />
              {t('frugal.menu.bankEmail')}
            </button>
            <button 
              onClick={() => {
                if (onOpenSubscription) onOpenSubscription();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 rounded-lg"
            >
              <FileText className="w-4 h-4" />
              {t('frugal.menu.subscription')}
            </button>
            <button 
              onClick={onLanguageChange}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 rounded-lg"
            >
              <Globe className="w-4 h-4" />
              {t('frugal.menu.language')} ({currentLanguage.toUpperCase()})
            </button>
            <button 
              onClick={() => {
                onOpenZenLock?.();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 rounded-lg"
            >
              <Shield className="w-4 h-4" />
              {t('frugal.menu.zenLock')}
            </button>
          </div>
        )}

        {/* MES ACTUAL */}
        <div className="px-3 py-1 mt-2">
          <button
            onClick={() => toggleSection('month')}
            className="w-full flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider"
          >
            <span className="flex items-center gap-2">
              <Calendar className="w-3 h-3" />
              {t('frugal.menu.currentMonth')}
            </span>
            <ChevronDown className={`w-3 h-3 transition-transform ${expandedSection === 'month' ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {expandedSection === 'month' && (
          <div className="px-2 py-1 space-y-1">
            <button 
              onClick={() => {
                onOpenMonthSummary?.();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 rounded-lg"
            >
              <FileText className="w-4 h-4" />
              {t('frugal.menu.monthSummary')}
            </button>
            <button 
              onClick={() => {
                onOpenCashflow?.();
                onClose();
              }}
              data-testid="menu-cashflow-btn"
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 rounded-lg"
            >
              <Activity className="w-4 h-4" />
              {t('frugal.menu.cashflow')}
            </button>
            <button 
              onClick={() => {
                onOpenMonthEmails?.();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 rounded-lg"
            >
              <Mail className="w-4 h-4" />
              {t('frugal.menu.monthEmails')}
            </button>
            <button 
              onClick={() => {
                onCloseMonth?.();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-emerald-400 hover:bg-emerald-500/10 rounded-lg"
            >
              <Calendar className="w-4 h-4" />
              {t('frugal.menu.closeMonth')}
            </button>
          </div>
        )}

        {/* PRÓXIMOS MESES */}
        <div className="px-3 py-1 mt-2">
          <button
            onClick={() => toggleSection('upcoming')}
            className="w-full flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider"
          >
            <span className="flex items-center gap-2">
              <CalendarClock className="w-3 h-3" />
              {t('frugal.menu.upcomingMonths')}
            </span>
            <ChevronDown className={`w-3 h-3 transition-transform ${expandedSection === 'upcoming' ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {expandedSection === 'upcoming' && (
          <div className="px-2 py-1 space-y-1">
            <button 
              onClick={() => {
                onOpenScheduledCharges?.();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 rounded-lg"
            >
              <Calendar className="w-4 h-4" />
              {t('frugal.menu.scheduledCharges')}
            </button>
          </div>
        )}

        {/* HISTORIAL */}
        <div className="px-3 py-1 mt-2">
          <button
            onClick={() => toggleSection('history')}
            className="w-full flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider"
            data-testid="menu-history-section-toggle"
          >
            <span className="flex items-center gap-2">
              <History className="w-3 h-3" />
              {t('frugal.menu.history')}
            </span>
            <ChevronDown className={`w-3 h-3 transition-transform ${expandedSection === 'history' ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {expandedSection === 'history' && (
          <div className="px-2 py-1 space-y-1">
            <button 
              onClick={() => {
                onOpenReportsHistory?.();
                onClose();
              }}
              data-testid="menu-reports-history-btn"
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 rounded-lg"
            >
              <FileText className="w-4 h-4" />
              {t('frugal.menu.reportsHistory')}
            </button>
          </div>
        )}

        <div className="border-t border-white/10 my-2" />

        {/* Soporte WhatsApp */}
        <button 
          onClick={() => {
            onOpenSupport?.();
            onClose();
          }}
          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-emerald-400 hover:bg-emerald-500/10"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          {t('frugal.menu.support')}
        </button>

        {/* Cerrar Sesión */}
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-rose-400 hover:bg-white/5"
        >
          <LogOut className="w-4 h-4" />
          {t('frugal.menu.logout')}
        </button>
      </div>
    </>
  );
};

export default UserMenu;
